import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, Search, Trash2, Building2, Mail, Phone,
  CheckCircle2, XCircle, Grid3x3, List as ListIcon, Table as TableIcon, Store, RefreshCw, Edit,
  MapPin, Map as MapIcon, Package, Euro, User, MessageSquare, UserPlus, UserMinus, Shield, Calendar,
  Download, Upload, FileSpreadsheet, X, Loader2, Settings, ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Medal, SlidersHorizontal
} from 'lucide-react';
import { AddButton } from '@/components/ui/AddButton';
import { useAuth } from '@/contexts/AuthContext';
import { useAlert } from '@/components/AlertProvider';
import { useConfirm } from '@/components/ConfirmDialog';
import ClientCreateModal from '@/components/clients/ClientCreateModal';
import { ColumnConfigurator, ColumnConfig } from '@/components/ui/ColumnConfigurator';
import api from '@/services/api';

interface Client {
  id: number;
  number?: number;
  display_number?: string;
  company_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  country: string | null;
  maps_link?: string | null;
  pappers_url?: string | null;
  infogreffe_url?: string | null;
  pharmanity_url?: string | null;
  website?: string | null;
  rdv_status?: 'R0' | 'R1' | 'R2' | 'R3' | null;
  cote?: 'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | null;
  gold_medal?: boolean;
  is_active: boolean;
  status: 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme';
  client_type: 'pharmacie' | 'parapharmacie' | 'centrale' | 'magasin_bio' | 'therapeute' | 'member' | 'independent' | 'central';
  admin_id: number | null;
  admin?: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
  };
  created_at: string;
  updated_at: string;
  last_login: string | null;
  orders_count?: number;
  total_ca?: number;
}

interface Admin {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
}

type ClientMapEntry = {
  item: Client;
  coords: { lat: number; lng: number } | null;
  addressLabel: string;
};

const getStatusHexColor = (status: string): string => {
  switch (status) {
    case 'prospect': return '#9333ea'; // purple-600
    case 'actif': return '#10b981';    // emerald-500
    case 'inactif': return '#eab308';   // yellow-500
    case 'bloque': return '#ef4444';    // red-500
    case 'ferme': return '#71717a';     // zinc-500
    default: return '#3b82f6';         // blue-500
  }
};

const createCustomMarkerIcon = (L: any, status: string) => {
  const color = getStatusHexColor(status);
  const svg = `
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="${color}" stroke="white" stroke-width="1.5"/>
      <circle cx="12" cy="9" r="2.5" fill="white"/>
    </svg>
  `;

  return L.divIcon({
    html: svg,
    className: 'custom-marker-icon',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const getStatusStyle = (status: string): string => {
  switch (status) {
    case 'prospect': return 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300';
    case 'actif': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300';
    case 'inactif': return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
    case 'bloque': return 'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300';
    case 'ferme': return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
};

const extractLatLngFromMapsLink = (mapsLink?: string | null) => {
  if (!mapsLink) return null;
  const atMatch = mapsLink.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (atMatch) {
    const lat = Number(atMatch[1]);
    const lng = Number(atMatch[2]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  }
  const queryMatch = mapsLink.match(/[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (queryMatch) {
    const lat = Number(queryMatch[1]);
    const lng = Number(queryMatch[2]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  }
  return null;
};

const buildClientAddressLabel = (client: Client): string => {
  const parts = [client.address, client.postal_code, client.city, client.country].filter(Boolean);
  return parts.join(', ');
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'prospect': return 'Prospect';
    case 'actif': return 'Actif';
    case 'inactif': return 'Inactif';
    case 'bloque': return 'Bloqué';
    case 'ferme': return 'Fermé';
    default: return status;
  }
};

const getBuyerTypeLabel = (type: string): string => {
  switch (type) {
    case 'pharmacie': return 'Pharmacie';
    case 'parapharmacie': return 'Parapharmacie';
    case 'centrale':
    case 'central': return 'Centrale d\'Achat';
    case 'magasin_bio': return 'Magasin Bio';
    case 'therapeute': return 'Thérapeute';
    case 'member': return 'Client Membre';
    case 'independent': return 'Indépendant';
    default: return type;
  }
};

const getBuyerTypeStyle = (type: string): string => {
  switch (type) {
    case 'pharmacie': return 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300';
    case 'parapharmacie': return 'bg-teal-100 text-teal-700 dark:bg-teal-950/30 dark:text-teal-300';
    case 'centrale':
    case 'central': return 'bg-orange-100 text-orange-700 dark:bg-orange-950/30 dark:text-orange-300';
    case 'magasin_bio': return 'bg-lime-100 text-lime-700 dark:bg-lime-950/30 dark:text-lime-300';
    case 'therapeute': return 'bg-purple-100 text-purple-700 dark:bg-purple-950/30 dark:text-purple-300';
    case 'member': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/30 dark:text-cyan-300';
    case 'independent': return 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
};

const ClientsListTailwind: React.FC = () => {
  const { user } = useAuth();
  const { showError, showSuccess } = useAlert();
  const { showConfirm } = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin: boolean = user?.role === 'super_admin';
  const isAdmin: boolean = user?.role === 'admin';
  const isApporteur: boolean = user?.role === 'apporteur';
  const isManager: boolean = isSuperAdmin || isAdmin || isApporteur;
  const isGlobalAdmin: boolean = isSuperAdmin || isAdmin;
  const searchStorageKey = 'clients_table_search';
  const filterStorageKey = 'clients_table_filters';
  const viewStorageKey = 'clients_table_view_mode';
  const storedViewMode = (() => {
    const saved = localStorage.getItem(viewStorageKey);
    if (saved === 'grid' || saved === 'list' || saved === 'table' || saved === 'map') return saved;
    return window.innerWidth < 640 ? 'list' : 'table';
  })();
  const storedFilters = (() => {
    try {
      return JSON.parse(localStorage.getItem(filterStorageKey) || '{}') as {
        statusFilter?: 'all' | 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme';
        mineOnlyFilter?: boolean;
        apporteurFilter?: number | null;
        clientTypeFilter?: string;
        cityFilter?: string;
        rxFilter?: 'R0' | 'R1' | 'R2' | 'R3' | '';
        coteFilter?: 'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | '';
        assignedProspectsMode?: boolean;
        rdvR1PlusMode?: boolean;
        goldMedalOnly?: boolean;
      };
    } catch (e) {
      console.error('Error parsing saved filters:', e);
      return {};
    }
  })();

  const [items, setItems] = useState<Client[]>([]);
  const [mapItems, setMapItems] = useState<any[]>([]);
  const [missingCoordsItems, setMissingCoordsItems] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [_statusStats, setStatusStats] = useState<Record<string, number>>({ prospect: 0, actif: 0, inactif: 0, bloque: 0, ferme: 0 });
  const [globalStats, setGlobalStats] = useState<Record<string, number>>({ prospect: 0, actif: 0, inactif: 0, bloque: 0, ferme: 0 });
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(200);
  const [apporteurs, setApporteurs] = useState<Admin[]>([]);
  const [busy, setBusy] = useState<boolean>(false);
  const [search, setSearch] = useState<string>(() => localStorage.getItem(searchStorageKey) || '');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'table' | 'map'>(storedViewMode);
  const [statusFilter, setStatusFilter] = useState<'all' | 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme'>(storedFilters.statusFilter ?? 'all');
  const [mineOnlyFilter, setMineOnlyFilter] = useState<boolean>(storedFilters.mineOnlyFilter ?? false);
  const [apporteurFilter, setApporteurFilter] = useState<number | null>(storedFilters.apporteurFilter ?? null);
  const [clientTypeFilter, setClientTypeFilter] = useState<string>(storedFilters.clientTypeFilter ?? '');
  const [cityFilter, setCityFilter] = useState<string>(storedFilters.cityFilter ?? '');
  const [rxFilter, setRxFilter] = useState<'R0' | 'R1' | 'R2' | 'R3' | ''>(storedFilters.rxFilter ?? '');
  const [coteFilter, setCoteFilter] = useState<'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | ''>(storedFilters.coteFilter ?? '');
  const [assignedProspectsMode, setAssignedProspectsMode] = useState<boolean>(storedFilters.assignedProspectsMode ?? false);
  const [rdvR1PlusMode, setRdvR1PlusMode] = useState<boolean>(storedFilters.rdvR1PlusMode ?? false);
  const [goldMedalOnly, setGoldMedalOnly] = useState<boolean>(storedFilters.goldMedalOnly ?? false);
  const [rdvStatusFilter, setRdvStatusFilter] = useState<'R1' | 'R2+' | null>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [urlFiltersReady, setUrlFiltersReady] = useState<boolean>(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);
  const markersLayerRef = useRef<any>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapRefreshBusy, setMapRefreshBusy] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState<{ total: number; done: number; updated: number; failed: number } | null>(null);
  const geocodeStopRef = useRef(false);
  const shouldCenterNextRef = useRef(false);
  const isInternalMapMove = useRef(false);
  const lastBboxRef = useRef<string>('');
  const lastExtentRef = useRef<any>(null);
  const mapFiltersRef = useRef({
    search: '',
    statusFilter: 'all' as 'all' | 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme',
    apporteurFilter: null as number | null,
    clientTypeFilter: '',
    cityFilter: '',
    rxFilter: '' as 'R0' | 'R1' | 'R2' | 'R3' | '',
    coteFilter: '' as 'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | '',
    mineOnlyFilter: false,
    assignedProspectsMode: false,
    rdvR1PlusMode: false,
    goldMedalOnly: false
  });

  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const defaultColumns: ColumnConfig[] = [
      { key: 'company_name', label: 'Entreprise', visible: true, sortable: true },
      { key: 'status', label: 'Statut', visible: true, sortable: true },
      { key: 'first_name', label: 'Prénom', visible: true, sortable: true },
      { key: 'last_name', label: 'Nom', visible: true, sortable: true },
      { key: 'email', label: 'Email', visible: true, sortable: true },
      { key: 'apporteur', label: 'Apporteur', visible: isGlobalAdmin, sortable: true },
      { key: 'phone', label: 'Téléphone', visible: true, sortable: false },
      { key: 'postal_code', label: 'Code Postal', visible: true, sortable: true },
      { key: 'city', label: 'Ville', visible: true, sortable: true },
      { key: 'type', label: 'Type', visible: true, sortable: true },
      { key: 'pappers', label: 'Pappers.fr', visible: true, sortable: false },
      { key: 'maps', label: 'Carte', visible: true, sortable: false },
      { key: 'website', label: 'WEB', visible: true, sortable: false },
      { key: 'rdv_status', label: 'Rx', visible: true, sortable: true },
      { key: 'cote', label: 'Cote', visible: true, sortable: true },
      { key: 'orders', label: 'Commandes', visible: false, sortable: true },
      { key: 'ca', label: 'CA', visible: false, sortable: true },
      { key: 'created_at', label: 'Créé le', visible: false, sortable: true },
      { key: 'actions', label: 'Actions', visible: true, sortable: false },
    ];

    const saved = localStorage.getItem('clients_table_columns');
    if (saved) {
      try {
        const parsed: ColumnConfig[] = JSON.parse(saved);
        const parsedByKey = new Map(parsed.map((col) => [col.key, col]));
        return defaultColumns.map((col) => parsedByKey.get(col.key) ?? col);
      } catch (e) {
        console.error('Error parsing saved columns:', e);
      }
    }
    return defaultColumns;
  });
  const [showColumnConfigurator, setShowColumnConfigurator] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  // Fonction pour rendre une cellule de header selon la colonne
  const renderTableHeader = (col: ColumnConfig) => {
    const baseClass = "px-6 py-4 whitespace-nowrap bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20";

    switch (col.key) {
      case 'company_name':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('client')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Client</span>
              {sortColumn === 'client' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'status':
        return (
          <th key={col.key} className={`${baseClass} text-center`}>
            <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Statut</span>
              {sortColumn === 'status' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'first_name':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('firstName')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Prénom</span>
              {sortColumn === 'firstName' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'last_name':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('lastName')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Nom</span>
              {sortColumn === 'lastName' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'phone':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('phone')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Téléphone</span>
              {sortColumn === 'phone' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'email':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('email')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Email</span>
              {sortColumn === 'email' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'postal_code':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('postalCode')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Code Postal</span>
              {sortColumn === 'postalCode' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'city':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('city')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Ville</span>
              {sortColumn === 'city' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'type':
        return <th key={col.key} className={`${baseClass} text-center`}>Type</th>;
      case 'pappers':
        return <th key={col.key} className={`${baseClass} text-center`}>Pappers.fr</th>;
      case 'maps':
        return <th key={col.key} className={`${baseClass} text-center`}>Carte</th>;
      case 'website':
        return <th key={col.key} className={`${baseClass} text-center`}>WEB</th>;
      case 'rdv_status':
        return (
          <th key={col.key} className={`${baseClass} text-center`}>
            <button onClick={() => handleSort('rdv_status')} className="flex items-center gap-1 mx-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>R<sub>x</sub></span>
              {sortColumn === 'rdv_status' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'cote':
        return (
          <th key={col.key} className={`${baseClass} text-center`}>
            <button onClick={() => handleSort('cote')} className="flex items-center gap-1 mx-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Cote</span>
              {sortColumn === 'cote' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'orders':
        return (
          <th key={col.key} className={`${baseClass} text-right`}>
            <button onClick={() => handleSort('orders')} className="flex items-center gap-1 ml-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Commandes</span>
              {sortColumn === 'orders' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'ca':
        return (
          <th key={col.key} className={`${baseClass} text-right`}>
            <button onClick={() => handleSort('ca')} className="flex items-center gap-1 ml-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>CA Total</span>
              {sortColumn === 'ca' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'created_at':
        return (
          <th key={col.key} className={`${baseClass} text-center`}>
            <button onClick={() => handleSort('createdDate')} className="flex items-center gap-1 mx-auto hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Date Création</span>
              {sortColumn === 'createdDate' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'apporteur':
        return (
          <th key={col.key} className={`${baseClass} text-left`}>
            <button onClick={() => handleSort('apporteur')} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              <span>Apporteur</span>
              {sortColumn === 'apporteur' ? (sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
            </button>
          </th>
        );
      case 'actions':
        return <th key={col.key} className={`${baseClass} text-right`}>Actions</th>;
      default:
        return null;
    }
  };

  const [showModal, setShowModal] = useState<boolean>(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);

  // Expose navigate to window for map popup actions without full reload
  useEffect(() => {
    (window as any).handleMapClientClick = (id: number) => {
      navigate(`/achat/clients/${id}`);
    };
    return () => {
      delete (window as any).handleMapClientClick;
    };
  }, [navigate]);

  // Fonction pour rendre une cellule de body selon la colonne
  const renderTableCell = (col: ColumnConfig, item: Client) => {
    const ordersCount = item.orders_count || 0;
    const totalCa = item.total_ca || 0;

    switch (col.key) {
      case 'company_name':
        return (
          <td key={col.key} className="px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="font-bold text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{item.company_name}</span>
            </div>
          </td>
        );
      case 'status':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusStyle(item.status)}`}>
              {getStatusLabel(item.status)}
            </span>
          </td>
        );
      case 'first_name':
        return <td key={col.key} className="px-6 py-4 text-slate-600 dark:text-slate-400 font-medium">{item.first_name || '—'}</td>;
      case 'last_name':
        return <td key={col.key} className="px-6 py-4 text-slate-600 dark:text-slate-400 font-medium">{item.last_name || '—'}</td>;
      case 'phone':
        return <td key={col.key} className="px-6 py-4 text-slate-600 dark:text-slate-400 font-medium">{item.phone || '—'}</td>;
      case 'email':
        return <td key={col.key} className="px-6 py-4 text-slate-600 dark:text-slate-400">{item.email}</td>;
      case 'postal_code':
        return <td key={col.key} className="px-6 py-4 text-slate-600 dark:text-slate-400 font-medium">{item.postal_code || '—'}</td>;
      case 'city':
        return <td key={col.key} className="px-6 py-4 text-slate-600 dark:text-slate-400 font-medium truncate max-w-[150px]" title={item.city || ''}>{item.city || '—'}</td>;
      case 'type':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getBuyerTypeStyle(item.client_type)}`}>
              {getBuyerTypeLabel(item.client_type)}
            </span>
          </td>
        );
      case 'pappers':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            {item.pappers_url ? (
              <a
                href={item.pappers_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/30 dark:hover:bg-blue-950/50 rounded-lg transition-colors"
                title="Voir sur Pappers.fr"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span>Pappers</span>
              </a>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
            )}
          </td>
        );
      case 'maps':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            {item.maps_link ? (
              <a
                href={item.maps_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50 rounded-lg transition-colors"
                title="Voir la carte"
              >
                <MapPin className="h-3.5 w-3.5" />
                <span>Carte</span>
              </a>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
            )}
          </td>
        );
      case 'website':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            {item.website ? (
              <a
                href={item.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/30 dark:hover:bg-purple-950/50 rounded-lg transition-colors"
                title="Voir le site web"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span>Voir</span>
              </a>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
            )}
          </td>
        );
      case 'rdv_status':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            {item.rdv_status ? (
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                {item.rdv_status}
              </span>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
            )}
          </td>
        );
      case 'cote':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            {item.cote ? (
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${item.cote === 'Chaud' ? 'bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300' :
                item.cote === 'Tiède' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-300' :
                  item.cote === 'Glacier' ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300' :
                    'bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300'
                }`}>
                {item.cote}
              </span>
            ) : (
              <span className="text-xs text-slate-400 dark:text-slate-600">—</span>
            )}
          </td>
        );
      case 'orders':
        return (
          <td key={col.key} className="px-6 py-4 text-right">
            <div className="flex items-center justify-end gap-1.5 font-semibold text-slate-700 dark:text-slate-300">
              <Package className="h-3.5 w-3.5 text-slate-400" />
              {ordersCount}
            </div>
          </td>
        );
      case 'ca':
        return (
          <td key={col.key} className="px-6 py-4 text-right font-bold text-emerald-600 dark:text-emerald-400">
            {totalCa.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€
          </td>
        );
      case 'created_at':
        return (
          <td key={col.key} className="px-6 py-4 text-center">
            <div className="flex items-center justify-center gap-1.5 text-slate-600 dark:text-slate-400">
              <Calendar className="h-3.5 w-3.5 text-blue-400" />
              <span className="text-sm">{new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            </div>
          </td>
        );
      case 'apporteur':
        return (
          <td key={col.key} className="px-6 py-4 text-slate-600 dark:text-slate-400">
            {getApporteurFullName(item) ? (
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-[10px] font-bold text-blue-600 dark:text-blue-400">
                  {getApporteurFullName(item).charAt(0)}
                </div>
                <span className="text-xs">{getApporteurFullName(item)}</span>
              </div>
            ) : (
              <span className="text-xs italic text-slate-400">Non assigné</span>
            )}
          </td>
        );
      case 'actions':
        return (
          <td key={col.key} className="px-6 py-4 text-right">
            <div className="flex justify-end gap-1">
              {isApporteur && item.status === 'prospect' && !item.admin_id && (
                <button
                  onClick={(e) => handleSelfAssign(e, item)}
                  className="p-2 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-lg transition-colors"
                  title="S'affecter le prospect"
                >
                  <UserPlus className="h-4 w-4" />
                </button>
              )}
              {isApporteur && item.status === 'prospect' && item.admin_id === user?.id && (
                <button
                  onClick={(e) => handleSelfUnassign(e, item)}
                  className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Se désaffecter du prospect"
                >
                  <UserMinus className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); openModal(item); }}
                className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                title="Modifier"
                aria-label="Modifier"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); del(item); }}
                className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title="Supprimer"
                aria-label="Supprimer"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </td>
        );
      default:
        return null;
    }
  };
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState('');
  const [importResult, setImportResult] = useState<{ success: boolean; created: number; skipped: number; errors: string[]; message: string } | null>(null);

  const loadApporteurs = async (): Promise<void> => {
    try {
      const res = await api.get('/users/admins');
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : (raw?.items || raw?.results || []);
      setApporteurs(list);
    } catch (e) {
      console.error('Load apporteurs error:', e);
    }
  };

  const refreshGlobalStats = async () => {
    try {
      const res = await api.get('/clients', { params: { skip: 0, limit: 1 } });
      const data = res.data;
      if (data && typeof data === 'object' && 'stats' in data && data.stats) {
        setGlobalStats(data.stats);
      }
    } catch (e) { /* ignore */ }
  };

  const load = async (bbox?: { minLat: number, maxLat: number, minLng: number, maxLng: number }): Promise<void> => {
    // If we have a bbox, check if it's the same as the last one to avoid infinite loops
    if (bbox) {
      const bboxString = `${bbox.minLat.toFixed(4)},${bbox.maxLat.toFixed(4)},${bbox.minLng.toFixed(4)},${bbox.maxLng.toFixed(4)}`;
      if (bboxString === lastBboxRef.current) return;
      lastBboxRef.current = bboxString;
    } else {
      // If no bbox, it's a "hard" refresh (filter change), we should center the map after loading
      lastBboxRef.current = '';
      shouldCenterNextRef.current = true;
    }

    setBusy(true);
    try {
      if (viewMode === 'map') {
        const filters = mapFiltersRef.current;
        const params: Record<string, unknown> = {};
        if (filters.search) params.search = filters.search;
        if (filters.statusFilter !== 'all') params.status = filters.statusFilter;
        if (filters.assignedProspectsMode) params.assigned_prospects = true;
        if (filters.rdvR1PlusMode) params.rdv_r1_plus = true;
        if (isGlobalAdmin && filters.apporteurFilter) params.admin_id = filters.apporteurFilter;
        if (filters.clientTypeFilter) params.client_type = filters.clientTypeFilter;
        if (filters.mineOnlyFilter) params.mine_only = true;
        if (filters.goldMedalOnly) params.gold_medal = true;

        if (bbox) {
          params.min_lat = bbox.minLat;
          params.max_lat = bbox.maxLat;
          params.min_lng = bbox.minLng;
          params.max_lng = bbox.maxLng;
        }

        const res = await api.get('/clients/all/map', { params });
        setMapItems(res.data.items || []);

        // Only update missing coords items if we fetched everything (no bbox)
        if (!bbox) {
          const missing = (res.data.items || []).filter((item: any) => !item.lat || !item.lng);
          setMissingCoordsItems(missing);

          // Store extent for later if map not ready yet
          if (res.data.extent) {
            lastExtentRef.current = res.data.extent;
          }

          // Use the extent from backend to center the map if available
          if (res.data.extent && leafletMapRef.current && shouldCenterNextRef.current) {
            const { min_lat, max_lat, min_lng, max_lng } = res.data.extent;
            const bounds = (window as any).L.latLngBounds([min_lat, min_lng], [max_lat, max_lng]);

            isInternalMapMove.current = true;
            leafletMapRef.current.fitBounds(bounds.pad(0.1), { animate: false });
            shouldCenterNextRef.current = false;

            setTimeout(() => {
              isInternalMapMove.current = false;
              const finalBounds = leafletMapRef.current.getBounds();
              const finalBbox = {
                minLat: finalBounds.getSouth(),
                maxLat: finalBounds.getNorth(),
                minLng: finalBounds.getWest(),
                maxLng: finalBounds.getEast()
              };
              void load(finalBbox);
            }, 600);
          }
        }

        setTotal(res.data.total || 0);
        if (res.data.stats) {
          setStatusStats(res.data.stats);
          if (filters.statusFilter === 'all' && !filters.search && !filters.assignedProspectsMode && !filters.rdvR1PlusMode && !filters.apporteurFilter && !filters.clientTypeFilter && !filters.mineOnlyFilter) {
            setGlobalStats(res.data.stats);
          }
        }
        setBusy(false);
        return;
      }

      const params: Record<string, unknown> = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (assignedProspectsMode) params.assigned_prospects = true;
      if (rdvR1PlusMode) params.rdv_r1_plus = true;
      if (rdvStatusFilter) params.rdv_status = rdvStatusFilter;
      if (mineOnlyFilter) params.mine_only = true;
      if (isGlobalAdmin && apporteurFilter) params.admin_id = apporteurFilter;
      if (clientTypeFilter) params.client_type = clientTypeFilter;
      if (cityFilter) params.city = cityFilter;
      if (rxFilter) params.rdv_status = rxFilter;
      if (coteFilter) params.cote = coteFilter;
      if (goldMedalOnly) params.gold_medal = true;

      const res = await api.get('/clients', { params });
      const data = res.data;

      if (data && typeof data === 'object' && 'items' in data) {
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotal(data.total || 0);
        if (data.stats) {
          setStatusStats(data.stats);
          if (statusFilter === 'all' && !search && !assignedProspectsMode && !rdvR1PlusMode && !apporteurFilter && !clientTypeFilter && !cityFilter && !rxFilter && !coteFilter && !mineOnlyFilter && !goldMedalOnly) {
            setGlobalStats(data.stats);
          }
        }
      } else {
        setItems(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      console.error('Load clients error:', e);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const adminIdParam = searchParams.get('admin_id');
    const statusParam = searchParams.get('status');
    const clientIdParam = searchParams.get('id');
    const rdvR1PlusParam = searchParams.get('rdv_r1_plus');
    const rdvStatusParam = searchParams.get('rdv_status');

    if (clientIdParam) {
      const loadOne = async () => {
        try {
          const res = await api.get(`/clients/${clientIdParam}`);
          openModal(res.data);
        } catch (e) {
          console.error('Error loading client from URL:', e);
        }
      };
      void loadOne();
    }

    if (!adminIdParam && !statusParam) {
      setUrlFiltersReady(true);
      return;
    }

    const parsedAdminId = adminIdParam ? Number(adminIdParam) : null;
    const normalizedStatus = statusParam?.toLowerCase();
    const allowedStatuses = ['prospect', 'actif', 'inactif', 'bloque', 'ferme'] as const;
    const nextStatus = allowedStatuses.includes(normalizedStatus as typeof allowedStatuses[number])
      ? (normalizedStatus as typeof allowedStatuses[number])
      : 'all';

    setSearch('');
    setClientTypeFilter('');
    setCityFilter('');
    setRxFilter('');
    setCoteFilter('');
    setMineOnlyFilter(false);
    setGoldMedalOnly(false);
    setStatusFilter(nextStatus);
    setApporteurFilter(parsedAdminId && !Number.isNaN(parsedAdminId) ? parsedAdminId : null);
    setRdvR1PlusMode(rdvR1PlusParam === 'true');
    setRdvStatusFilter(rdvStatusParam === 'R1' || rdvStatusParam === 'R2+' ? rdvStatusParam : null);
    setAssignedProspectsMode(false);
    setSearchParams({});
    setUrlFiltersReady(true);
  }, [searchParams, setSearchParams]);

  // Fetch global stats once on mount (unfiltered) so stat cards always show correct totals
  useEffect(() => {
    if (!urlFiltersReady) return;
    void refreshGlobalStats();
  }, [urlFiltersReady]);

  useEffect(() => {
    if (!urlFiltersReady) return;
    setPage(1);
    void loadApporteurs();
    void load();
  }, [statusFilter, apporteurFilter, clientTypeFilter, cityFilter, rxFilter, coteFilter, mineOnlyFilter, assignedProspectsMode, rdvR1PlusMode, goldMedalOnly, rdvStatusFilter, urlFiltersReady, viewMode]);


  useEffect(() => {
    if (!urlFiltersReady) return;
    void load();
  }, [page, urlFiltersReady]);

  useEffect(() => {
    if (!urlFiltersReady) return;
    const t = setTimeout(() => {
      setPage(1);
      void load();
    }, 300);
    return () => clearTimeout(t);
  }, [search, urlFiltersReady]);

  useEffect(() => {
    localStorage.setItem(searchStorageKey, search);
  }, [search]);

  useEffect(() => {
    localStorage.setItem(viewStorageKey, viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem(filterStorageKey, JSON.stringify({
      statusFilter,
      mineOnlyFilter,
      apporteurFilter,
      clientTypeFilter,
      cityFilter,
      rxFilter,
      coteFilter,
      assignedProspectsMode,
      rdvR1PlusMode,
      goldMedalOnly
    }));
  }, [statusFilter, mineOnlyFilter, apporteurFilter, clientTypeFilter, cityFilter, rxFilter, coteFilter, assignedProspectsMode, rdvR1PlusMode, goldMedalOnly]);

  useEffect(() => {
    mapFiltersRef.current = {
      search,
      statusFilter,
      apporteurFilter,
      clientTypeFilter,
      cityFilter,
      rxFilter,
      coteFilter,
      mineOnlyFilter,
      assignedProspectsMode,
      rdvR1PlusMode,
      goldMedalOnly
    };
  }, [search, statusFilter, apporteurFilter, clientTypeFilter, cityFilter, rxFilter, coteFilter, mineOnlyFilter, assignedProspectsMode, rdvR1PlusMode, goldMedalOnly]);

  const openModal = (item?: Client): void => {
    setEditing(item || null);
    setShowModal(true);
  };

  const activeFilters = useMemo(() => {
    const tags: Array<{ key: string; label: string; onClear: () => void }> = [];
    if (search.trim()) tags.push({ key: 'search', label: `Recherche: ${search}`, onClear: () => setSearch('') });
    if (statusFilter !== 'all') tags.push({ key: 'status', label: `Statut: ${getStatusLabel(statusFilter)}`, onClear: () => setStatusFilter('all') });
    if (assignedProspectsMode) tags.push({ key: 'assignedProspects', label: 'Prospects assignés', onClear: () => setAssignedProspectsMode(false) });
    if (rdvR1PlusMode) tags.push({ key: 'rdvR1Plus', label: 'Prospects R1+', onClear: () => setRdvR1PlusMode(false) });
    if (rdvStatusFilter) tags.push({ key: 'rdvStatus', label: `Prospects ${rdvStatusFilter}`, onClear: () => setRdvStatusFilter(null) });
    if (mineOnlyFilter) tags.push({ key: 'mineOnly', label: 'Mes clients', onClear: () => setMineOnlyFilter(false) });
    if (apporteurFilter) {
      const apporteur = apporteurs.find(a => a.id === apporteurFilter);
      tags.push({
        key: 'apporteur',
        label: `Apporteur: ${apporteur ? `${apporteur.first_name} ${apporteur.last_name}` : apporteurFilter}`,
        onClear: () => setApporteurFilter(null)
      });
    }
    if (clientTypeFilter) tags.push({ key: 'clientType', label: `Type: ${getBuyerTypeLabel(clientTypeFilter)}`, onClear: () => setClientTypeFilter('') });
    if (cityFilter) tags.push({ key: 'city', label: `Ville: ${cityFilter}`, onClear: () => setCityFilter('') });
    if (rxFilter) tags.push({ key: 'rx', label: `Rx: ${rxFilter}`, onClear: () => setRxFilter('') });
    if (coteFilter) tags.push({ key: 'cote', label: `Cote: ${coteFilter}`, onClear: () => setCoteFilter('') });
    if (goldMedalOnly) tags.push({ key: 'goldMedal', label: "Médailles d'or", onClear: () => setGoldMedalOnly(false) });
    return tags;
  }, [search, statusFilter, assignedProspectsMode, rdvR1PlusMode, rdvStatusFilter, mineOnlyFilter, apporteurFilter, clientTypeFilter, cityFilter, rxFilter, coteFilter, goldMedalOnly, apporteurs]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getApporteurDisplayName = (item: Client): string => {
    if (item.admin) {
      const lastName = item.admin.last_name?.toUpperCase() || '';
      const firstName = item.admin.first_name || '';
      const name = [lastName, firstName].filter(Boolean).join(' ').trim();
      return name || item.admin.email || '';
    }
    const fromList = apporteurs.find((a) => a.id === item.admin_id);
    if (fromList) {
      const lastName = fromList.last_name?.toUpperCase() || '';
      const firstName = fromList.first_name || '';
      const name = [lastName, firstName].filter(Boolean).join(' ').trim();
      return name || fromList.email || '';
    }
    return '';
  };

  const getApporteurFullName = (item: Client): string => {
    return getApporteurDisplayName(item);
  };

  const toggleAssignedProspectsMode = () => {
    setAssignedProspectsMode((prev) => {
      const next = !prev;
      if (next) {
        setStatusFilter('prospect');
        setSortColumn('apporteur');
        setSortDirection('asc');
      } else {
        setStatusFilter('all');
        setSortColumn((current) => (current === 'apporteur' ? null : current));
      }
      return next;
    });
  };

  const sortedItems = useMemo(() => {
    const base = items;

    if (!sortColumn) return base;

    const sorted = [...base].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortColumn) {
        case 'client':
          aVal = a.company_name?.toLowerCase() || '';
          bVal = b.company_name?.toLowerCase() || '';
          break;
        case 'firstName':
          aVal = a.first_name?.toLowerCase() || '';
          bVal = b.first_name?.toLowerCase() || '';
          break;
        case 'lastName':
          aVal = a.last_name?.toLowerCase() || '';
          bVal = b.last_name?.toLowerCase() || '';
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'postalCode':
          aVal = a.postal_code || '';
          bVal = b.postal_code || '';
          break;
        case 'city':
          aVal = a.city?.toLowerCase() || '';
          bVal = b.city?.toLowerCase() || '';
          break;
        case 'orders':
          aVal = a.orders_count || 0;
          bVal = b.orders_count || 0;
          break;
        case 'ca':
          aVal = a.total_ca || 0;
          bVal = b.total_ca || 0;
          break;
        case 'createdDate':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'apporteur':
          aVal = getApporteurDisplayName(a).toLowerCase();
          bVal = getApporteurDisplayName(b).toLowerCase();
          break;
        case 'rdv_status':
          const rdvOrder: Record<string, number> = { 'R0': 0, 'R1': 1, 'R2': 2, 'R3': 3 };
          aVal = a.rdv_status ? rdvOrder[a.rdv_status] || 99 : 99;
          bVal = b.rdv_status ? rdvOrder[b.rdv_status] || 99 : 99;
          break;
        case 'cote':
          const coteOrder = { 'Chaud': 1, 'Tiède': 2, 'Froid': 3, 'Glacier': 4 };
          aVal = a.cote ? coteOrder[a.cote] || 99 : 99;
          bVal = b.cote ? coteOrder[b.cote] || 99 : 99;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [items, sortColumn, sortDirection, assignedProspectsMode, apporteurs]);

  const mapEntries = useMemo<ClientMapEntry[]>(() => {
    if (viewMode !== 'map') {
      return sortedItems.map((item) => ({
        item,
        coords: extractLatLngFromMapsLink(item.maps_link),
        addressLabel: buildClientAddressLabel(item)
      }));
    }

    // In map mode, we combine mapItems (visible markers) and missingCoordsItems (sidebar)
    // to build the full map summary
    const combined = [...mapItems];

    // Add missing coords items if they aren't already in mapItems (they shouldn't be due to backend filtering)
    missingCoordsItems.forEach(item => {
      if (!combined.find(m => m.id === item.id)) {
        combined.push(item);
      }
    });

    return combined.map((item) => ({
      item,
      coords: (item.lat && item.lng) ? { lat: item.lat, lng: item.lng } : null,
      addressLabel: buildClientAddressLabel(item)
    }));
  }, [sortedItems, mapItems, missingCoordsItems, viewMode]);

  const mapSummary = useMemo(() => {
    const withCoords = mapEntries.filter((entry) => entry.coords);
    const withoutCoords = mapEntries.filter((entry) => !entry.coords);
    const prospect = mapEntries.filter((entry) => entry.item.status === 'prospect').length;
    const actif = mapEntries.filter((entry) => entry.item.status === 'actif').length;
    const inactif = mapEntries.filter((entry) => entry.item.status === 'inactif').length;
    const bloque = mapEntries.filter((entry) => entry.item.status === 'bloque').length;

    return {
      total: mapEntries.length,
      withCoords,
      withoutCoords,
      stats: {
        prospect,
        actif,
        inactif,
        bloque
      }
    };
  }, [mapEntries]);

  useEffect(() => {
    if (viewMode !== 'map') return;

    let isMounted = true;
    const loadLeaflet = async () => {
      if (!mapRef.current || leafletMapRef.current) return;
      setMapLoading(true);
      try {
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }

        if (!document.querySelector('link[href*="MarkerCluster.css"]')) {
          const clusterLink = document.createElement('link');
          clusterLink.rel = 'stylesheet';
          clusterLink.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.css';
          document.head.appendChild(clusterLink);
          const clusterDefaultLink = document.createElement('link');
          clusterDefaultLink.rel = 'stylesheet';
          clusterDefaultLink.href = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/MarkerCluster.Default.css';
          document.head.appendChild(clusterDefaultLink);
        }

        if (!(window as any).L) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!(window as any).L.markerClusterGroup) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet.markercluster@1.4.1/dist/leaflet.markercluster.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!isMounted || !mapRef.current) return;

        const L = (window as any).L;
        const map = L.map(mapRef.current).setView([46.6, 2.5], 5.6);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        markersLayerRef.current = L.layerGroup().addTo(map);
        leafletMapRef.current = map;

        // Add listeners for moveend and zoomend to re-fetch data based on bbox with debouncing
        let moveTimeout: any;
        map.on('moveend zoomend', () => {
          if (isInternalMapMove.current) return;

          if (moveTimeout) clearTimeout(moveTimeout);
          moveTimeout = setTimeout(() => {
            if (isInternalMapMove.current) return;

            const bounds = map.getBounds();
            const bbox = {
              minLat: bounds.getSouth(),
              maxLat: bounds.getNorth(),
              minLng: bounds.getWest(),
              maxLng: bounds.getEast()
            };
            void load(bbox);
          }, 400);
        });

        setMapReady(true);
        // Force a size recalculation after the map is in the DOM
        map.invalidateSize();
      } catch (error) {
        console.error('Failed to load map:', error);
        if (isMounted) setMapReady(false);
      } finally {
        if (isMounted) setMapLoading(false);
      }
    };

    void loadLeaflet();

    return () => {
      isMounted = false;
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      markersLayerRef.current = null;
      setMapReady(false);
    };
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'map') return;
    if (!mapReady) return;
    const map = leafletMapRef.current;
    const L = (window as any).L;
    if (!map || !L) return;

    setMapLoading(true);

    if (!markersLayerRef.current) {
      markersLayerRef.current = L.layerGroup().addTo(map);
    }

    markersLayerRef.current.clearLayers();
    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      chunkedLoading: true,
      maxClusterRadius: 30,
      disableClusteringAtZoom: 8,
      spiderfyDistanceMultiplier: 1.5
    });

    const bounds = L.latLngBounds([]);
    const entriesWithCoords = mapItems.filter(item => item.lat && item.lng);

    entriesWithCoords.forEach((item) => {
      const marker = L.marker([item.lat, item.lng], {
        icon: createCustomMarkerIcon(L, item.status)
      });
      const popupContent = `
        <div style="min-width:180px" class="p-2">
          <div class="font-bold text-slate-900 mb-1">${item.company_name}</div>
          <div class="text-xs text-slate-500 mb-2">${item.address || item.city || ''}</div>
          <div class="flex items-center gap-2">
            <span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getStatusStyle(item.status)}">
              ${getStatusLabel(item.status)}
            </span>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getBuyerTypeStyle(item.client_type)}">
              ${getBuyerTypeLabel(item.client_type)}
            </span>
          </div>
          <button 
            onclick="handleMapClientClick(${item.id})"
            class="mt-3 w-full px-3 py-1.5 bg-blue-600 text-white text-[10px] font-bold rounded hover:bg-blue-700 transition-colors"
          >
            VOIR DÉTAILS
          </button>
        </div>
      `;
      marker.bindPopup(popupContent.trim());
      clusterGroup.addLayer(marker);
      bounds.extend([item.lat, item.lng]);
    });

    markersLayerRef.current.addLayer(clusterGroup);

    // Only fitBounds if shouldCenterNextRef.current is true (i.e. after a filter change, not a map move)
    if (shouldCenterNextRef.current) {
      shouldCenterNextRef.current = false;
      isInternalMapMove.current = true;

      if (entriesWithCoords.length > 0 || lastExtentRef.current) {
        const targetBounds = entriesWithCoords.length > 0
          ? bounds
          : L.latLngBounds([lastExtentRef.current.min_lat, lastExtentRef.current.min_lng], [lastExtentRef.current.max_lat, lastExtentRef.current.max_lng]);

        map.fitBounds(targetBounds.pad(0.1), { animate: false });
      } else {
        map.setView([46.6, 2.5], 5, { animate: false });
      }

      // Release the lock after a short delay AND trigger a refresh with the actual bbox
      setTimeout(() => {
        isInternalMapMove.current = false;
        const finalBounds = map.getBounds();
        const finalBbox = {
          minLat: finalBounds.getSouth(),
          maxLat: finalBounds.getNorth(),
          minLng: finalBounds.getWest(),
          maxLng: finalBounds.getEast()
        };
        void load(finalBbox);
      }, 600);
    }

    requestAnimationFrame(() => setMapLoading(false));
  }, [mapSummary, viewMode, mapReady]);

  const handleRefreshMapLinks = async (): Promise<void> => {
    const totalToProcess = mapSummary.withoutCoords.length;
    if (totalToProcess === 0) return;

    setMapRefreshBusy(true);
    geocodeStopRef.current = false;
    setGeocodeProgress({ total: totalToProcess, done: 0, updated: 0, failed: 0 });

    const BATCH_SIZE = 10;
    let totalUpdated = 0;
    let totalFailed = 0;
    let totalDone = 0;
    let consecutiveEmpty = 0;

    try {
      while (totalDone < totalToProcess && consecutiveEmpty < 3 && !geocodeStopRef.current) {
        const response = await api.post('/clients/maps/refresh', {
          limit: BATCH_SIZE
        }, { timeout: 120000 });
        const data = response.data || {};
        const batchUpdated = data.updated ?? 0;
        const batchFailed = data.failed ?? 0;
        const batchProcessed = data.processed ?? 0;

        totalUpdated += batchUpdated;
        totalFailed += batchFailed;
        totalDone += batchProcessed;

        setGeocodeProgress({ total: totalToProcess, done: totalDone, updated: totalUpdated, failed: totalFailed });

        if (batchProcessed === 0 || (batchUpdated === 0 && batchFailed === 0)) {
          consecutiveEmpty++;
        } else {
          consecutiveEmpty = 0;
        }

        if (batchProcessed < BATCH_SIZE) break;
      }

      if (geocodeStopRef.current) {
        showSuccess(`Géolocalisation arrêtée. ${totalUpdated} localisé(s) sur ${totalDone} traité(s).`);
      } else if (totalUpdated > 0) {
        showSuccess(`${totalUpdated} client(s) géolocalisé(s) sur ${totalDone} traité(s)`);
      } else if (totalFailed > 0) {
        showError(`Aucune adresse géolocalisée sur ${totalDone} traitée(s). Vérifiez les adresses.`);
      } else {
        showSuccess('Aucun client à géolocaliser');
      }
      await load();
    } catch (e) {
      showError('Erreur lors de la mise à jour des coordonnées');
    } finally {
      setMapRefreshBusy(false);
      setTimeout(() => setGeocodeProgress(null), 3000);
    }
  };

  const del = async (item: Client): Promise<void> => {
    const confirmed = await showConfirm({
      title: 'Supprimer le client',
      message: `Êtes-vous sûr de vouloir supprimer l'client "${item.company_name}" ? Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error'
    });
    if (!confirmed) return;
    try {
      await api.delete(`/clients/${item.id}`);
      showSuccess('Client supprimé avec succès');
      await load();
      void refreshGlobalStats();
    } catch (e) {
      showError('Erreur lors de la suppression du client');
    }
  };

  const stats = {
    total: Object.values(globalStats).reduce((a, b) => a + b, 0),
    prospect: globalStats.prospect || 0,
    actif: globalStats.actif || 0,
    inactif: globalStats.inactif || 0,
    bloque: globalStats.bloque || 0,
    ferme: globalStats.ferme || 0,
  } as const;

  const isMapView = viewMode === 'map';
  const showLoadingState = !isMapView && busy;
  const showEmptyState = !isMapView && sortedItems.length === 0;

  const assignedProspectsCount = useMemo(() => {
    if (assignedProspectsMode) return total;
    const source = viewMode === 'map' ? mapItems : items;
    return source.filter((item) => item.status === 'prospect' && (Boolean(item.admin_id) || Boolean(item.admin))).length;
  }, [assignedProspectsMode, total, items, mapItems, viewMode]);


  const handleExportCSV = async () => {
    try {
      const response = await api.get('/clients/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Export CSV téléchargé avec succès');
    } catch (e) {
      showError('Erreur lors de l\'export');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/clients/export/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_clients.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError('Erreur lors du téléchargement du template');
    }
  };

  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    setImportProgress(0);
    setImportStatus('Préparation du fichier...');

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImportProgress(20);
      setImportStatus('Envoi du fichier...');

      const response = await api.post('/clients/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total);
            setImportProgress(20 + percentCompleted);
            setImportStatus(`Envoi: ${percentCompleted * 2}%`);
          }
        }
      });

      setImportProgress(80);
      setImportStatus('Traitement des données...');

      await new Promise(resolve => setTimeout(resolve, 300));

      setImportProgress(100);
      setImportStatus('Import terminé!');
      setImportResult(response.data);

      if (response.data.created > 0) {
        showSuccess(response.data.message);
        void load();
      }
    } catch (e: any) {
      const errorMessage = e.response?.data?.detail || 'Erreur lors de l\'import';
      showError(errorMessage);
      setImportResult({ success: false, created: 0, skipped: 0, errors: [errorMessage], message: errorMessage });
      setImportProgress(0);
      setImportStatus('');
    } finally {
      setTimeout(() => {
        setImporting(false);
        setImportProgress(0);
        setImportStatus('');
      }, 1000);
      event.target.value = '';
    }
  };

  const resetAllFilters = () => {
    setSearch('');
    localStorage.removeItem(searchStorageKey);
    localStorage.removeItem(filterStorageKey);
    setStatusFilter('all');
    setAssignedProspectsMode(false);
    setRdvR1PlusMode(false);
    setRdvStatusFilter(null);
    setMineOnlyFilter(false);
    setApporteurFilter(null);
    setClientTypeFilter('');
    setCityFilter('');
    setRxFilter('');
    setCoteFilter('');
    setSortColumn(null);
    setSortDirection('asc');
  };


  const handleSelfAssign = async (e: React.MouseEvent, item: Client) => {
    e.stopPropagation();
    const confirmed = await showConfirm({
      title: 'S\'affecter le prospect',
      message: `Voulez-vous vous affecter le prospect "${item.company_name}" ? Il ne sera plus visible par les autres apporteurs.`,
      confirmText: 'S\'affecter',
      cancelText: 'Annuler',
      severity: 'info'
    });
    if (!confirmed) return;
    try {
      await api.post(`/clients/${item.id}/self-assign`);
      showSuccess('Prospect affecté avec succès');
      await load();
      void refreshGlobalStats();
    } catch (e) {
      showError('Erreur lors de l\'affectation du prospect');
    }
  };

  const handleSelfUnassign = async (e: React.MouseEvent, item: Client) => {
    e.stopPropagation();
    const confirmed = await showConfirm({
      title: 'Se désaffecter du prospect',
      message: `Voulez-vous vous désaffecter du prospect "${item.company_name}" ? Il redeviendra visible par les autres apporteurs.`,
      confirmText: 'Se désaffecter',
      cancelText: 'Annuler',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await api.post(`/clients/${item.id}/self-unassign`);
      showSuccess('Prospect désaffecté avec succès');
      await load();
      void refreshGlobalStats();
    } catch (e) {
      showError('Erreur lors de la désaffectation du prospect');
    }
  };

  return (
    <div className={`h-full bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950 overflow-auto md:overflow-hidden md:flex md:flex-col`}>
      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Importer des clients</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Format CSV (; ) ou XLSX</p>
                </div>
              </div>
              <button
                onClick={() => { setShowImportModal(false); setImportResult(null); setImportProgress(0); setImportStatus(''); }}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                title="Fermer"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">Colonnes requises</h3>
                <p className="text-sm text-blue-700 dark:text-blue-400">
                  <strong>company_name</strong> et <strong>email</strong> sont obligatoires.
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-500 mt-2">
                  Colonnes optionnelles: marque, first_name, last_name, invoice_email, phone, address, city, postal_code, country, siret, pappers_url, client_type, status, payment_mode, payment_terms
                </p>
              </div>

              <button
                onClick={handleDownloadTemplate}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-400 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors"
              >
                <Download className="h-4 w-4" />
                Télécharger le template CSV
              </button>

              <div className="relative">
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleImportCSV}
                  disabled={importing}
                  aria-label="Sélectionner un fichier CSV/XLSX"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                />
                <div className={`flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-lg transition-colors ${importing ? 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50' : 'border-emerald-300 dark:border-emerald-700 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:border-emerald-600 dark:hover:bg-emerald-950/30'}`}>
                  {importing ? (
                    <>
                      <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">{importStatus}</span>
                      <div className="w-full max-w-xs mt-2">
                        <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-300 ease-out"
                            style={{ width: `${importProgress}%` }}
                          />
                        </div>
                        <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-1">{importProgress}%</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-emerald-500" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Cliquez ou glissez un fichier CSV/XLSX ici</span>
                    </>
                  )}
                </div>
              </div>

              {importResult && (
                <div className={`rounded-lg p-4 ${importResult.created > 0 ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800' : 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {importResult.created > 0 ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <XCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    )}
                    <span className={`font-semibold ${importResult.created > 0 ? 'text-emerald-800 dark:text-emerald-300' : 'text-amber-800 dark:text-amber-300'}`}>
                      {importResult.message}
                    </span>
                  </div>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">Erreurs:</p>
                      <ul className="text-xs text-amber-600 dark:text-amber-500 space-y-0.5">
                        {importResult.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <button
                onClick={() => { setShowImportModal(false); setImportResult(null); setImportProgress(0); setImportStatus(''); }}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-6 py-2 sm:py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-lg">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
            <Building2 className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Clients</h1>
        </div>
        {isManager && (
          <div className="flex items-center gap-1.5 sm:gap-2">
            {isGlobalAdmin && (
              <div className="flex items-center gap-0.5 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 sm:p-1">
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 px-2 py-1.5 sm:px-3 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-md transition-colors"
                  title="Exporter les clients en CSV"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-1.5 px-2 py-1.5 sm:px-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-md transition-colors"
                  title="Importer des clients depuis un CSV"
                >
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Import</span>
                </button>
              </div>
            )}
            <AddButton
              onClick={() => openModal()}
              className="w-auto"
            />
          </div>
        )}
      </div>

      {/* Stats Cards — Mobile: vertical centered like client orders, Desktop: horizontal */}
      <div className="md:hidden px-3 py-2">
        <div className="grid grid-cols-3 gap-1.5">
          <button onClick={() => setStatusFilter('all')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'all' ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Users className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-bold text-slate-800 dark:text-slate-100">{stats.total}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Total</span>
          </button>
          <button onClick={() => setStatusFilter('prospect')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'prospect' ? 'border-purple-400 bg-purple-50 dark:bg-purple-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Users className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
            <span className="text-xs font-bold text-purple-600 dark:text-purple-400">{assignedProspectsMode ? assignedProspectsCount : stats.prospect}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Prospects</span>
          </button>
          <button onClick={() => setStatusFilter('actif')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'actif' ? 'border-green-400 bg-green-50 dark:bg-green-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">{stats.actif}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Actifs</span>
          </button>
          <button onClick={() => setStatusFilter('inactif')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'inactif' ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <XCircle className="h-3.5 w-3.5 text-yellow-500 dark:text-yellow-400" />
            <span className="text-xs font-bold text-yellow-600 dark:text-yellow-300">{stats.inactif}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Inactifs</span>
          </button>
          <button onClick={() => setStatusFilter('bloque')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'bloque' ? 'border-red-400 bg-red-50 dark:bg-red-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Shield className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
            <span className="text-xs font-bold text-red-600 dark:text-red-400">{stats.bloque}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Bloqués</span>
          </button>
          <button onClick={() => setStatusFilter('ferme')} className={`flex flex-col items-center gap-0.5 rounded-lg border py-1.5 transition-all ${statusFilter === 'ferme' ? 'border-zinc-400 bg-zinc-50 dark:bg-zinc-950/30 shadow-sm' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}`}>
            <Users className="h-3.5 w-3.5 text-zinc-600 dark:text-zinc-400" />
            <span className="text-xs font-bold text-zinc-700 dark:text-zinc-200">{stats.ferme}</span>
            <span className="text-[8px] uppercase font-semibold text-slate-400">Fermés</span>
          </button>
        </div>
      </div>

      {/* Desktop stats cards */}
      <div className="hidden md:grid px-6 py-4 grid-cols-6 gap-3">
        <button
          onClick={() => setStatusFilter('all')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'all'
            ? 'border-blue-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-blue-200/80'} px-4 py-3 text-left transition-all duration-200`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-blue-50/70 to-indigo-50/70 dark:from-blue-950/20 dark:to-indigo-950/20 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300"><Users className="h-3.5 w-3.5" /></div>
              <div className="flex flex-col"><span className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">Total</span></div>
            </div>
            <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{stats.total}</div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('prospect')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'prospect'
            ? 'border-purple-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-purple-200/80'} px-4 py-3 text-left transition-all duration-200`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-purple-50/70 to-fuchsia-50/70 dark:from-purple-950/20 dark:to-fuchsia-950/20 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-300"><Users className="h-3.5 w-3.5" /></div>
              <div className="flex flex-col"><span className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">Prospects</span></div>
            </div>
            <div className="text-sm font-bold text-purple-600 dark:text-purple-400">
              {assignedProspectsMode ? assignedProspectsCount : stats.prospect}</div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('actif')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'actif'
            ? 'border-green-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-green-200/80'} px-4 py-3 text-left transition-all duration-200`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 dark:from-emerald-950/20 dark:to-teal-950/20 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" /></div>
              <div className="flex flex-col"><span className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">Actifs</span></div>
            </div>
            <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{stats.actif}</div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('inactif')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'inactif'
            ? 'border-yellow-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-yellow-300/80'} px-4 py-3 text-left transition-all duration-200`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-yellow-50/70 to-amber-50/70 dark:from-yellow-900/30 dark:to-amber-800/30 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-50 text-yellow-500 dark:bg-yellow-900/60 dark:text-yellow-300"><XCircle className="h-3.5 w-3.5" /></div>
              <div className="flex flex-col"><span className="text-[10px] font-semibold uppercase text-yellow-600 dark:text-yellow-400">Inactifs</span></div>
            </div>
            <div className="text-sm font-bold text-yellow-600 dark:text-yellow-300">{stats.inactif}</div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('bloque')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'bloque'
            ? 'border-red-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-red-200/80'} px-4 py-3 text-left transition-all duration-200`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-red-50/70 to-orange-50/70 dark:from-red-950/20 dark:to-orange-950/20 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300"><Shield className="h-3.5 w-3.5" /></div>
              <div className="flex flex-col"><span className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">Bloqués</span></div>
            </div>
            <div className="text-sm font-bold text-red-600 dark:text-red-400">{stats.bloque}</div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('ferme')}
          className={`group relative overflow-hidden rounded-2xl border ${statusFilter === 'ferme'
            ? 'border-zinc-400 shadow-md bg-white/90 dark:bg-slate-900/90'
            : 'border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 hover:border-zinc-200/80'} px-4 py-3 text-left transition-all duration-200`}
        >
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-zinc-50/70 to-slate-50/70 dark:from-zinc-950/20 dark:to-slate-950/20 pointer-events-none transition-opacity duration-200" />
          <div className="relative flex items-center justify-between gap-1 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-50 text-zinc-700 dark:bg-zinc-950/40 dark:text-zinc-300">
                <div className="relative h-3.5 w-3.5">
                  <User className="h-3.5 w-3.5" />
                  <XCircle className="absolute -right-1 -bottom-1 h-2.5 w-2.5 text-red-600 dark:text-red-300" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400">Fermés</span>
              </div>
            </div>
            <div className="text-sm font-bold text-zinc-700 dark:text-zinc-200">{stats.ferme}</div>
          </div>
        </button>
      </div>

      {/* Filters – desktop layout (lg+) */}
      <div className="hidden lg:flex px-6 py-3 flex-row flex-wrap xl:flex-nowrap items-center gap-3">
        {isGlobalAdmin && (
          <button
            onClick={toggleAssignedProspectsMode}
            className={`h-10 w-auto px-4 rounded-lg border flex items-center gap-2 text-sm font-medium transition-colors shadow-sm shrink-0 ${assignedProspectsMode
              ? 'bg-orange-500 text-white border-orange-600 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-500 dark:border-orange-700'
              : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50 dark:bg-transparent dark:text-orange-300 dark:border-orange-800 dark:hover:bg-orange-950/30'
              } ring-1 ring-orange-300 dark:ring-orange-900/40`}
          >
            <Users className="h-4 w-4" />
            Prospects assignés
          </button>
        )}
        {isApporteur && (
          <button
            onClick={() => setMineOnlyFilter(!mineOnlyFilter)}
            className={`h-10 w-auto px-4 rounded-lg border flex items-center gap-2 text-sm font-medium transition-colors shadow-sm shrink-0 ${mineOnlyFilter
              ? 'bg-orange-500 text-white border-orange-600 hover:bg-orange-600 dark:bg-orange-600 dark:hover:bg-orange-500 dark:border-orange-700'
              : 'bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100 dark:bg-orange-950/20 dark:text-orange-300 dark:border-orange-800 dark:hover:bg-orange-950/30'
              } ring-1 ring-orange-300 dark:ring-orange-900/40`}
          >
            <User className="h-4 w-4" />
            Mes clients
          </button>
        )}
        <button
          onClick={() => setGoldMedalOnly(!goldMedalOnly)}
          className={`h-10 w-auto px-4 rounded-lg border flex items-center gap-2 text-sm font-medium transition-colors shadow-sm shrink-0 ${goldMedalOnly
            ? 'bg-amber-500 text-white border-amber-600 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500 dark:border-amber-700'
            : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50 dark:bg-transparent dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-950/30'
            } ring-1 ring-amber-300 dark:ring-amber-900/40`}
        >
          <Medal className="h-4 w-4" />
          Nos Médailles d&apos;or
        </button>
        <div className="relative flex-1 min-w-[240px] xl:min-w-[280px] min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom, email, code postal, commune..." className="w-full h-10 pl-10 pr-4 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex flex-wrap gap-3 w-auto flex-wrap xl:flex-nowrap">
          {isGlobalAdmin && (
            <select
              value={apporteurFilter || ''}
              onChange={(e) => setApporteurFilter(e.target.value ? parseInt(e.target.value) : null)}
              title="Filtrer par apporteur"
              aria-label="Filtrer par apporteur"
              className="h-10 flex-none w-44 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">AA/AC</option>
              {apporteurs.filter(a => (a as any).role === 'apporteur').map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
            </select>
          )}
          <select
            value={clientTypeFilter}
            onChange={(e) => setClientTypeFilter(e.target.value)}
            title="Filtrer par type"
            aria-label="Filtrer par type"
            className="h-10 flex-none w-40 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Types</option>
            <option value="pharmacie">Pharmacie</option>
            <option value="parapharmacie">Parapharmacie</option>
            <option value="centrale">Centrale d'Achat</option>
            <option value="magasin_bio">Magasin Bio</option>
            <option value="therapeute">Thérapeute</option>
            <option value="member">Client Membre</option>
            <option value="independent">Indépendant</option>
          </select>
          <div className="relative flex-none w-36">
            <input
              type="text"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              placeholder="Ville"
              title="Filtrer par ville"
              aria-label="Filtrer par ville"
              className="w-full h-10 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={rxFilter}
            onChange={(e) => setRxFilter(e.target.value as 'R0' | 'R1' | 'R2' | 'R3' | '')}
            title="Filtrer par Rx"
            aria-label="Filtrer par Rx"
            className="h-10 flex-none w-28 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Rx</option>
            <option value="R0">R0</option>
            <option value="R1">R1</option>
            <option value="R2">R2</option>
            <option value="R3">R3</option>
          </select>
          <select
            value={coteFilter}
            onChange={(e) => setCoteFilter(e.target.value as 'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | '')}
            title="Filtrer par Cote"
            aria-label="Filtrer par Cote"
            className="h-10 flex-none w-32 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Cote</option>
            <option value="Glacier">Glacier</option>
            <option value="Froid">Froid</option>
            <option value="Tiède">Tiède</option>
            <option value="Chaud">Chaud</option>
          </select>
          <div className="flex items-center gap-2 flex-nowrap">
            <div className="flex shrink-0 gap-1 bg-white dark:bg-slate-800 p-1 rounded-lg border border-slate-300 dark:border-slate-600">
              <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><ListIcon className="h-4 w-4" /></button>
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><Grid3x3 className="h-4 w-4" /></button>
              <button onClick={() => setViewMode('table')} className={`p-2 rounded ${viewMode === 'table' ? 'bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><TableIcon className="h-4 w-4" /></button>
              <button onClick={() => setViewMode('map')} className={`p-2 rounded ${viewMode === 'map' ? 'bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><MapIcon className="h-4 w-4" /></button>
            </div>
            {viewMode === 'table' && (
              <button
                onClick={() => setShowColumnConfigurator(true)}
                className="h-10 shrink-0 px-4 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                title="Gérer les colonnes"
              >
                <Settings className="h-4 w-4" />
                <span>Colonnes</span>
              </button>
            )}
            <button
              onClick={resetAllFilters}
              className="h-10 shrink-0 px-4 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Filters – mobile layout (< lg): compact bar + drawer */}
      <div className="lg:hidden px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." className="w-full h-10 pl-10 pr-4 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex shrink-0 gap-1 bg-white dark:bg-slate-800 p-0.5 rounded-lg border border-slate-300 dark:border-slate-600">
            <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><ListIcon className="h-4 w-4" /></button>
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><Grid3x3 className="h-4 w-4" /></button>
            <button onClick={() => setViewMode('map')} className={`p-2 rounded ${viewMode === 'map' ? 'bg-purple-100 text-purple-600 dark:bg-purple-950/30 dark:text-purple-300' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}><MapIcon className="h-4 w-4" /></button>
          </div>
          <button
            onClick={() => setShowFilterDrawer(true)}
            className="relative h-10 shrink-0 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1.5"
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span className="text-sm font-medium">Filtres</span>
            {activeFilters.filter(f => f.key !== 'search').length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center px-1">
                {activeFilters.filter(f => f.key !== 'search').length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilterDrawer && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowFilterDrawer(false)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-t-2xl shadow-xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <span className="text-base font-semibold text-slate-800 dark:text-slate-100">Filtres</span>
              <button onClick={() => setShowFilterDrawer(false)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
              {isGlobalAdmin && (
                <button
                  onClick={toggleAssignedProspectsMode}
                  className={`h-10 w-full px-4 rounded-lg border flex items-center gap-2 text-sm font-medium transition-colors shadow-sm ${assignedProspectsMode
                    ? 'bg-orange-500 text-white border-orange-600'
                    : 'bg-white text-orange-700 border-orange-300 dark:bg-transparent dark:text-orange-300 dark:border-orange-800'
                    } ring-1 ring-orange-300 dark:ring-orange-900/40`}
                >
                  <Users className="h-4 w-4" />
                  Prospects assignés
                </button>
              )}
              {isApporteur && (
                <button
                  onClick={() => setMineOnlyFilter(!mineOnlyFilter)}
                  className={`h-10 w-full px-4 rounded-lg border flex items-center gap-2 text-sm font-medium transition-colors shadow-sm ${mineOnlyFilter
                    ? 'bg-orange-500 text-white border-orange-600'
                    : 'bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-950/20 dark:text-orange-300 dark:border-orange-800'
                    } ring-1 ring-orange-300 dark:ring-orange-900/40`}
                >
                  <User className="h-4 w-4" />
                  Mes clients
                </button>
              )}
              <button
                onClick={() => setGoldMedalOnly(!goldMedalOnly)}
                className={`h-10 w-full px-4 rounded-lg border flex items-center gap-2 text-sm font-medium transition-colors shadow-sm ${goldMedalOnly
                  ? 'bg-amber-500 text-white border-amber-600'
                  : 'bg-white text-amber-700 border-amber-300 dark:bg-transparent dark:text-amber-300 dark:border-amber-800'
                  } ring-1 ring-amber-300 dark:ring-amber-900/40`}
              >
                <Medal className="h-4 w-4" />
                Nos Médailles d&apos;or
              </button>
              {isGlobalAdmin && (
                <select
                  value={apporteurFilter || ''}
                  onChange={(e) => setApporteurFilter(e.target.value ? parseInt(e.target.value) : null)}
                  title="Filtrer par apporteur"
                  aria-label="Filtrer par apporteur"
                  className="h-10 w-full px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">AA/AC</option>
                  {apporteurs.filter(a => (a as any).role === 'apporteur').map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                </select>
              )}
              <select
                value={clientTypeFilter}
                onChange={(e) => setClientTypeFilter(e.target.value)}
                title="Filtrer par type"
                aria-label="Filtrer par type"
                className="h-10 w-full px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Types</option>
                <option value="pharmacie">Pharmacie</option>
                <option value="parapharmacie">Parapharmacie</option>
                <option value="centrale">Centrale d'Achat</option>
                <option value="magasin_bio">Magasin Bio</option>
                <option value="therapeute">Thérapeute</option>
                <option value="member">Client Membre</option>
                <option value="independent">Indépendant</option>
              </select>
              <input
                type="text"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                placeholder="Ville"
                title="Filtrer par ville"
                aria-label="Filtrer par ville"
                className="h-10 w-full px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex gap-3">
                <select
                  value={rxFilter}
                  onChange={(e) => setRxFilter(e.target.value as 'R0' | 'R1' | 'R2' | 'R3' | '')}
                  title="Filtrer par Rx"
                  aria-label="Filtrer par Rx"
                  className="h-10 flex-1 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Rx</option>
                  <option value="R0">R0</option>
                  <option value="R1">R1</option>
                  <option value="R2">R2</option>
                  <option value="R3">R3</option>
                </select>
                <select
                  value={coteFilter}
                  onChange={(e) => setCoteFilter(e.target.value as 'Glacier' | 'Froid' | 'Tiède' | 'Chaud' | '')}
                  title="Filtrer par Cote"
                  aria-label="Filtrer par Cote"
                  className="h-10 flex-1 px-3 rounded-lg border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Cote</option>
                  <option value="Glacier">Glacier</option>
                  <option value="Froid">Froid</option>
                  <option value="Tiède">Tiède</option>
                  <option value="Chaud">Chaud</option>
                </select>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
              <button
                onClick={() => { resetAllFilters(); setShowFilterDrawer(false); }}
                className="flex-1 h-10 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Réinitialiser
              </button>
              <button
                onClick={() => setShowFilterDrawer(false)}
                className="flex-1 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}

      {activeFilters.length > 0 && (
        <div className="px-4 sm:px-6 pb-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600 dark:text-slate-300 font-semibold">Filtres actifs:</span>
          {activeFilters.map((tag) => (
            <span key={tag.key} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-800 border border-blue-300 shadow-sm dark:bg-blue-950/50 dark:text-blue-200 dark:border-blue-700">
              {tag.label}
              <button
                type="button"
                onClick={tag.onClear}
                className="inline-flex items-center justify-center rounded-full p-0.5 text-blue-700 hover:bg-blue-200 hover:text-blue-900 dark:text-blue-200 dark:hover:bg-blue-900/50"
                aria-label={`Supprimer ${tag.label}`}
                title={`Supprimer ${tag.label}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={`flex-1 px-3 sm:px-6 pb-6 min-h-0 ${viewMode === 'map' ? 'md:flex md:flex-col overflow-hidden' : 'overflow-auto'}`}>
        {showLoadingState ? (
          <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-purple-600" /></div>
        ) : showEmptyState ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500"><Users className="h-16 w-16 mb-4 opacity-50" /><p className="text-lg font-medium">Aucun client trouvé</p></div>
        ) : (
          <div className={viewMode === 'map' ? 'md:flex md:flex-col md:flex-1 md:min-h-0' : 'space-y-6'}>
            {viewMode === 'list' ? (
              <div className="space-y-2 sm:space-y-3">
                {sortedItems.map((item) => {
                  const ordersCount = item.orders_count || 0;
                  const totalCa = item.total_ca || 0;
                  return (
                    <div
                      key={item.id}
                      onClick={() => navigate(`/achat/clients/${item.id}`)}
                      className="group p-2.5 sm:p-4 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 sm:hover:translate-x-1 transition-all duration-300 cursor-pointer"
                    >
                      {/* Mobile layout */}
                      <div className="sm:hidden">
                        <div className="flex items-start gap-2.5">
                          <div className="w-9 h-9 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg flex items-center justify-center shrink-0">
                            <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-sm text-slate-900 dark:text-slate-100 truncate">{item.company_name}</h3>
                              <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider shrink-0 ${getStatusStyle(item.status)}`}>
                                {getStatusLabel(item.status)}
                              </span>
                            </div>
                            <div className="mt-0.5 space-y-0.5 text-xs text-slate-500 dark:text-slate-400">
                              <div className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{item.email}</span></div>
                              <div className="flex items-center gap-2">
                                <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{item.city || '—'}</span>
                                {item.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{item.phone}</span>}
                              </div>
                              {getApporteurFullName(item) && (
                                <div className="flex items-center gap-1"><User className="h-3 w-3" />Apporteur: {getApporteurFullName(item)}</div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{totalCa.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</span>
                            <span className="text-[10px] text-slate-400 font-medium uppercase">{ordersCount} commandes</span>
                          </div>
                          <div className="flex gap-0.5">
                            {isApporteur && item.status === 'prospect' && !item.admin_id && (
                              <button onClick={(e) => handleSelfAssign(e, item)} className="p-1.5 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md" title="S'affecter le prospect"><UserPlus className="h-3.5 w-3.5" /></button>
                            )}
                            {isApporteur && item.status === 'prospect' && item.admin_id === user?.id && (
                              <button onClick={(e) => handleSelfUnassign(e, item)} className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md" title="Se désaffecter"><UserMinus className="h-3.5 w-3.5" /></button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); openModal(item); }} className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md" title="Modifier"><Edit className="h-3.5 w-3.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md" title="Supprimer"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      </div>
                      {/* Desktop layout */}
                      <div className="hidden sm:flex flex-row items-center gap-4">
                        <div className="flex items-center gap-4 min-w-0 flex-1">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                            <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">{item.company_name}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusStyle(item.status)}`}>
                                {getStatusLabel(item.status)}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-sm text-slate-500 dark:text-slate-400">
                              <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{item.email}</span>
                              {item.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{item.phone}</span>}
                              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{item.city || '—'}</span>
                              {getApporteurFullName(item) && (
                                <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />Apporteur: {getApporteurFullName(item)}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 border-l border-slate-200 dark:border-slate-700 pl-6">
                          <div className="text-right">
                            <div className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                              {totalCa.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">
                              {ordersCount} commandes
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {isApporteur && item.status === 'prospect' && !item.admin_id && (
                              <button
                                onClick={(e) => handleSelfAssign(e, item)}
                                className="p-2 text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-lg transition-colors"
                                title="S'affecter le prospect"
                              >
                                <UserPlus className="h-4 w-4" />
                              </button>
                            )}
                            {isApporteur && item.status === 'prospect' && item.admin_id === user?.id && (
                              <button
                                onClick={(e) => handleSelfUnassign(e, item)}
                                className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                                title="Se désaffecter du prospect"
                              >
                                <UserMinus className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); openModal(item); }}
                              className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Modifier"
                              aria-label="Modifier"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); del(item); }}
                              className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Supprimer"
                              aria-label="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-4">
                {sortedItems.map((item) => {
                  const ordersCount = item.orders_count || 0;
                  const totalCa = item.total_ca || 0;

                  return (
                    <div
                      key={item.id}
                      onClick={() => navigate(`/achat/clients/${item.id}`)}
                      className="group relative overflow-hidden bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl p-3 sm:p-6 shadow-sm border sm:border-2 border-slate-200 dark:border-slate-700 hover:shadow-xl sm:hover:scale-[1.02] hover:border-blue-300 dark:hover:border-blue-700 transition-all duration-300 cursor-pointer"
                    >
                      {/* Mobile card layout */}
                      <div className="sm:hidden">
                        <div className="flex items-start gap-2.5">
                          <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl flex items-center justify-center shrink-0">
                            <Building2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">{item.company_name}</h3>
                              <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider shrink-0 ${getStatusStyle(item.status)}`}>
                                {getStatusLabel(item.status)}
                              </span>
                            </div>
                            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold mt-0.5 ${getBuyerTypeStyle(item.client_type)}`}>
                              <Store className="h-3 w-3" />
                              {getBuyerTypeLabel(item.client_type)}
                            </div>
                            {getApporteurFullName(item) && (
                              <div className="mt-0.5 flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                                <User className="h-2.5 w-2.5 text-blue-500" />
                                Apporteur: {getApporteurFullName(item)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-400">
                          <div className="flex items-center gap-2"><User className="h-3 w-3 text-slate-400 shrink-0" /><span className="truncate">{`${item.first_name ?? ''} ${item.last_name ?? ''}`.trim() || '—'}</span></div>
                          <div className="flex items-center gap-2"><Mail className="h-3 w-3 text-emerald-500 shrink-0" /><span className="truncate">{item.email}</span></div>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-emerald-500" />{item.phone || '—'}</span>
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-emerald-500" />{item.city || '—'}</span>
                          </div>
                          <div className="flex items-center gap-2"><Calendar className="h-3 w-3 text-blue-500 shrink-0" />{new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                        </div>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{totalCa.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€</span>
                            <span className="text-[10px] text-slate-400 font-medium uppercase">{ordersCount} commandes</span>
                          </div>
                          <div className="flex gap-0.5">
                            <button onClick={(e) => { e.stopPropagation(); openModal(item); }} className="p-1.5 text-blue-600 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-400 rounded-md" title="Modifier"><Edit className="h-3.5 w-3.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/achat/clients/${item.id}?tab=notes`); }} className="p-1.5 text-orange-500 bg-orange-50/50 dark:bg-orange-950/30 dark:text-orange-400 rounded-md" title="Notes"><MessageSquare className="h-3.5 w-3.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/achat/clients/${item.id}?tab=pricing`); }} className="p-1.5 text-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/30 dark:text-emerald-400 rounded-md" title="BPU"><Euro className="h-3.5 w-3.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); navigate(`/achat/clients/${item.id}?tab=sales-points`); }} className="p-1.5 text-cyan-500 bg-cyan-50/50 dark:bg-cyan-950/30 dark:text-cyan-400 rounded-md" title="PDV"><Store className="h-3.5 w-3.5" /></button>
                            {isApporteur && item.status === 'prospect' && !item.admin_id && (
                              <button onClick={(e) => handleSelfAssign(e, item)} className="p-1.5 text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 rounded-md" title="S'affecter"><UserPlus className="h-3.5 w-3.5" /></button>
                            )}
                            {isApporteur && item.status === 'prospect' && item.admin_id === user?.id && (
                              <button onClick={(e) => handleSelfUnassign(e, item)} className="p-1.5 text-red-600 bg-red-50 dark:bg-red-950/30 rounded-md" title="Se désaffecter"><UserMinus className="h-3.5 w-3.5" /></button>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); del(item); }} className="p-1.5 text-red-500 bg-red-50 dark:bg-red-950/30 dark:text-red-400 rounded-md" title="Supprimer"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      </div>

                      {/* Desktop card layout */}
                      <div className="hidden sm:block">
                        {/* Header Section */}
                        <div className="flex items-start gap-4 mb-6">
                          <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl flex items-center justify-center shrink-0">
                            <Building2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div className="min-w-0 pt-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 truncate">
                                {item.company_name}
                              </h3>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusStyle(item.status)}`}>
                                {getStatusLabel(item.status)}
                              </span>
                            </div>
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${getBuyerTypeStyle(item.client_type)}`}>
                              <Store className="h-3.5 w-3.5" />
                              {getBuyerTypeLabel(item.client_type)}
                            </div>
                            {getApporteurFullName(item) && (
                              <div className="mt-2 flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                                <User className="h-3 w-3 text-blue-500" />
                                <span>Apporteur: {getApporteurFullName(item)}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Info Section */}
                        <div className="space-y-4 mb-8">
                          <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                            <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center shrink-0">
                              <User className="h-5 w-5 text-slate-400" />
                            </div>
                            <span className="font-medium truncate">{`${item.first_name ?? ''} ${item.last_name ?? ''}`.trim() || '—'}</span>
                          </div>
                          <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                            <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center shrink-0">
                              <Mail className="h-5 w-5 text-emerald-500" />
                            </div>
                            <span className="font-medium truncate">{item.email}</span>
                          </div>
                          <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                            <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center shrink-0">
                              <Phone className="h-5 w-5 text-emerald-500" />
                            </div>
                            <span className="font-medium">{item.phone || '—'}</span>
                          </div>
                          <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                            <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center shrink-0">
                              <MapPin className="h-5 w-5 text-emerald-500" />
                            </div>
                            <span className="font-medium truncate">{item.city || '—'}, France</span>
                          </div>
                          <div className="flex items-center gap-4 text-slate-600 dark:text-slate-400">
                            <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center shrink-0">
                              <Calendar className="h-5 w-5 text-blue-500" />
                            </div>
                            <span className="font-medium">{new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        </div>

                        {/* Actions Section */}
                        <div className="grid grid-cols-6 gap-2 pt-4 border-t border-slate-50 dark:border-slate-800">
                          <button
                            onClick={(e) => { e.stopPropagation(); openModal(item); }}
                            className="aspect-square flex items-center justify-center bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                            title="Modifier"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/achat/clients/${item.id}?tab=notes`); }}
                            className="aspect-square flex items-center justify-center bg-orange-50/50 text-orange-500 dark:bg-orange-950/30 dark:text-orange-400 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/50 transition-colors"
                            title="Notes"
                          >
                            <MessageSquare className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/achat/clients/${item.id}?tab=pricing`); }}
                            className="aspect-square flex items-center justify-center bg-emerald-50/50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/50 transition-colors"
                            title="Bordereau de prix"
                          >
                            <Euro className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/achat/clients/${item.id}?tab=sales-points`); }}
                            className="aspect-square flex items-center justify-center bg-cyan-50/50 text-cyan-500 dark:bg-cyan-950/30 dark:text-cyan-400 rounded-xl hover:bg-cyan-50 dark:hover:bg-cyan-900/50 transition-colors"
                            title="Point de ventes"
                          >
                            <Store className="h-5 w-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/achat/clients/${item.id}?tab=contacts`); }}
                            className="aspect-square flex items-center justify-center bg-purple-50/50 text-purple-500 dark:bg-purple-950/30 dark:text-purple-400 rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/50 transition-colors"
                            title="Contacts"
                          >
                            <UserPlus className="h-5 w-5" />
                          </button>
                          {isApporteur && item.status === 'prospect' && !item.admin_id && (
                            <button
                              onClick={(e) => handleSelfAssign(e, item)}
                              className="aspect-square flex items-center justify-center bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                              title="S'affecter le prospect"
                            >
                              <UserPlus className="h-5 w-5" />
                            </button>
                          )}
                          {isApporteur && item.status === 'prospect' && item.admin_id === user?.id && (
                            <button
                              onClick={(e) => handleSelfUnassign(e, item)}
                              className="aspect-square flex items-center justify-center bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                              title="Se désaffecter du prospect"
                            >
                              <UserMinus className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); del(item); }}
                            className="aspect-square flex items-center justify-center bg-red-50 text-red-500 dark:bg-red-950/30 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>

                        {/* Stats Overlay */}
                        <div className="mt-4 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1">
                          <div className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {ordersCount} commandes
                          </div>
                          <div className="flex items-center gap-1">
                            <Euro className="h-3 w-3" />
                            {totalCa.toLocaleString('fr-FR')}€ CA
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : viewMode === 'map' ? (
              <div className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border sm:border-2 border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden md:flex md:flex-col md:flex-1 md:min-h-0">
                <div className="border-b border-slate-200 dark:border-slate-700 shrink-0">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                      <MapIcon className="h-4 w-4 text-blue-500" />
                      Carte des clients
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleRefreshMapLinks}
                        disabled={mapRefreshBusy || mapSummary.withoutCoords.length === 0}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Mettre à jour les coordonnées manquantes"
                      >
                        {mapRefreshBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                        {mapRefreshBusy ? 'Géolocalisation...' : `Géolocaliser (${mapSummary.withoutCoords.length})`}
                      </button>
                      {mapRefreshBusy && (
                        <button
                          onClick={() => { geocodeStopRef.current = true; }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 bg-red-50/70 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                          title="Arrêter la géolocalisation"
                        >
                          <X className="h-3.5 w-3.5" />
                          Arrêter
                        </button>
                      )}
                    </div>
                  </div>
                  {geocodeProgress && (
                    <div className="px-4 pb-3 space-y-1.5">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="font-medium text-emerald-700 dark:text-emerald-300">
                          {mapRefreshBusy ? (
                            <span className="flex items-center gap-1.5">
                              <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                              </span>
                              Géolocalisation en cours...
                            </span>
                          ) : (
                            'Terminé !'
                          )}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400 tabular-nums">
                          {geocodeProgress.done}/{geocodeProgress.total} traités — {geocodeProgress.updated} localisé{geocodeProgress.updated > 1 ? 's' : ''}
                          {geocodeProgress.failed > 0 && `, ${geocodeProgress.failed} échoué${geocodeProgress.failed > 1 ? 's' : ''}`}
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                        {mapRefreshBusy && geocodeProgress.done === 0 ? (
                          <div className="h-full w-full rounded-full bg-gradient-to-r from-emerald-500/30 via-emerald-500 to-emerald-500/30 animate-pulse" />
                        ) : (
                          <div
                            className="h-full rounded-full transition-all duration-700 ease-out bg-gradient-to-r from-emerald-500 to-emerald-400"
                            style={{ width: `${geocodeProgress.total > 0 ? Math.max(2, (geocodeProgress.done / geocodeProgress.total) * 100) : 0}%` }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative md:flex-1 h-[calc(100vh-60px)] md:h-auto md:min-h-0">
                  <div ref={mapRef} className="h-full w-full" />
                  {(mapLoading || busy) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-slate-900/70">
                      <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-200">
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                        {busy ? 'Chargement des clients...' : 'Chargement des points...'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden flex flex-col max-h-[calc(100vh-28rem)]">
                <div className="overflow-x-auto overflow-y-auto flex-1">
                  <table className="w-full text-sm">
                    <thead className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 border-b-2 border-blue-200 dark:border-blue-800 sticky top-0 z-10">
                      <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                        {columns.filter(col => col.visible).map(col => renderTableHeader(col))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {sortedItems.map((item) => (
                        <tr key={item.id} onClick={() => navigate(`/achat/clients/${item.id}`)} className="group hover:bg-blue-50/50 dark:hover:bg-blue-950/20 cursor-pointer transition-colors">
                          {columns.filter(col => col.visible).map(col => renderTableCell(col, item))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pagination Controls */}
            {total > pageSize && viewMode !== 'map' && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-3 sm:px-4 py-3 sm:py-4 bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{(page - 1) * pageSize + 1}</span>–<span className="font-semibold text-slate-700 dark:text-slate-200">{Math.min(page * pageSize, total)}</span> sur <span className="font-semibold text-slate-700 dark:text-slate-200">{total}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Précédent
                  </button>
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    {Math.min(5, Math.ceil(total / pageSize)) > 1 && Array.from({ length: Math.ceil(total / pageSize) }, (_, i) => i + 1)
                      .filter(pageNum => {
                        const totalPages = Math.ceil(total / pageSize);
                        if (totalPages <= 7) return true;
                        return pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= page - 1 && pageNum <= page + 1);
                      })
                      .map((pageNum, i, arr) => {
                        return (
                          <React.Fragment key={pageNum}>
                            {i > 0 && arr[i - 1] !== pageNum - 1 && (
                              <span className="px-1 sm:px-2 text-slate-400 text-xs">...</span>
                            )}
                            <button
                              onClick={() => setPage(pageNum)}
                              className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-xs sm:text-sm font-medium rounded-lg transition-colors ${page === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                                }`}
                            >
                              {pageNum}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>
                  <button
                    onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
                    disabled={page === Math.ceil(total / pageSize)}
                    className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Column Configurator Modal */}
      <ColumnConfigurator
        isOpen={showColumnConfigurator}
        onClose={() => setShowColumnConfigurator(false)}
        columns={columns}
        onChange={setColumns}
        storageKey="clients_table_columns"
      />

      {/* Client Create/Edit Modal */}
      {showModal && (
        <ClientCreateModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditing(null);
          }}
          onSuccess={() => {
            void load();
            void refreshGlobalStats();
            setShowModal(false);
            setEditing(null);
          }}
          editingClient={editing}
        />
      )}
    </div>
  );
};

export default ClientsListTailwind;
